import { HoverableLinkSizes } from '@paywithextend/components'
import Button from 'ext-ui/components/Button/Button'
import React, { useState } from 'react'
import Box from 'src/components/Box/Box'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ManagerReviewSettingsContent from 'src/components/ManagerReviewSettings/ManagerReviewSettingsContent'
import ManagerReviewSettingsForm from 'src/components/ManagerReviewSettings/ManagerReviewSettingsForm'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { useQueryManagerReviewRules } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationsListModel } from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import { peopleMembersUrl } from 'src/navigation/urls'
import { useQueryOrgCurrencies } from 'src/queries/organizations/currencies.queries'
import { css } from 'styled-components'

interface ManagerReviewSettingsProps {
  user?: UserListModel
  organization: OrganizationsListModel
}

const ManagerReviewSettings: React.FC<ManagerReviewSettingsProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()
  const { user, organization } = props

  const { data: currencies, isPending: gettingOrganizationCurrencies } =
    useQueryOrgCurrencies({ orgId: organization.id })
  const { data: rules, isPending: gettingRules } = useQueryManagerReviewRules({
    orgId: organization.id,
  })
  const loadingRules = gettingRules || gettingOrganizationCurrencies
  const [isEditing, setIsEditing] = useState<boolean>(false)

  return (
    <Box
      className={'flex-column'}
      css={css`
        height: fit-content;
      `}
      noShadow
      outlineBorder
    >
      {loadingRules ? (
        <LoadingBackgroundPage />
      ) : (
        <div className={'flex-column gap-10'}>
          <div className={'flex-column gap-4'}>
            <div className={'flex-row justify-between'}>
              <span className={'font-h3'}>
                {translate('Manager Approvals')}
              </span>
              {user?.hasOrgAdminPermissions && !isEditing && (
                <Button
                  variant={'text'}
                  onClick={(): void => {
                    setIsEditing(true)
                  }}
                  aria-label={translate('Update')}
                  data-testid={'updateManagerApprovalsRulesBtn'}
                >
                  {translate('Update')}
                  <SvgUpdate
                    css={css`
                      transform: scale(0.7);
                    `}
                  />
                </Button>
              )}
            </div>
            <div className={'flex-column gap-4 font-sm text-secondary'}>
              <span>
                {translate(
                  'Enable manager review for employee transactions across your organization. Set your approval threshold to control which transactions need review. After enabling, assign managers in the ${people} tab.',
                  {
                    people: (
                      <HoverableLink
                        ariaLabelSource={'People'}
                        href={peopleMembersUrl()}
                        testId={'peopleLink'}
                        size={HoverableLinkSizes.Small}
                        regular
                        underline
                      >
                        {translate('People')}
                      </HoverableLink>
                    ),
                  },
                )}
              </span>
              <span>
                {translate('Visit our ${helpCenter} to learn more.', {
                  helpCenter: (
                    <HoverableLink
                      ariaLabelSource={'Help Center'}
                      href={
                        'https://support.paywithextend.com/hc/en-us/sections/33395568552599-Manager-Review'
                      }
                      testId={'helpCenterLink'}
                      size={HoverableLinkSizes.Small}
                      regular
                      underline
                    >
                      {translate('Help Center')}
                    </HoverableLink>
                  ),
                })}
              </span>
            </div>
          </div>
          {isEditing && currencies ? (
            <ManagerReviewSettingsForm
              rules={rules}
              currencies={currencies}
              closeForm={(): void => {
                setIsEditing(false)
              }}
            />
          ) : (
            <ManagerReviewSettingsContent rules={rules} />
          )}
        </div>
      )}
    </Box>
  )
}

export default ManagerReviewSettings
